// Live, interactive copy of what StudentPanel shows a student on /session/:id for the
// session types Home.jsx routes there: CPP, SQL Schema, Blockly Shared, and (same panel,
// Python run path) Auto Grouping and Helper/Helpee. Used by the creation wizard's right
// column (LiveStudentPreview), inside FitViewport.
//
// Built from StudentPanelShell and the read-only facsimile leaves (same DOM and classes as
// the real components, so the real stylesheets apply), with the code column made live:
//   - the editor is editable (CodeArea's CodeMirror, or a real BlockMirror for Blockly);
//   - Run executes the code through the same path CodeStu uses for the type and shows the
//     output and per-test results the way CodeStu / TestList / the SQL diff tables do;
//   - Reset Code / Latest code behave like CodeBar's, against preview-local state.
// The chat column stays a facsimile: these types only have PEER chat (session.enable_chat
// gates peer chat here, see sessionSettings.js), which needs a group of real students.
// There is no AI chat on these types, so the `chatEnabled` and `onBeforeChat` props of the
// live-preview contract are accepted and deliberately ignored.
//
// Safety: nothing is persisted. No code rows, submissions, chat messages, telemetry,
// consent, joins or storage writes; no app contexts. The only network calls are the
// student run endpoints, which are read-only on the server: runCPP (C++) and runCode (SQL),
// both needing session.id; Python runs locally in Pyodide (pythonRunner.js).

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Divider, Grid, Tooltip, Typography } from '@mui/material'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import HistoryIcon from '@mui/icons-material/History'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import HandshakeIcon from '@mui/icons-material/Handshake'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import CodeMirror from '@uiw/react-codemirror'
import { python } from '@codemirror/lang-python'
import { indentUnit } from '@codemirror/language'
import StudentPanelShell from '../StudentPanelShell'
import { studentPanelChatFacsimile } from '../StudentPanelPreview'
import {
  AppBarFacsimile,
  CodeStuFrame,
  OutputFacsimile,
  PeerInstructionFacsimile,
  SqlSchemaFacsimile,
  TaskCardFacsimile,
  TestTableFacsimile,
} from '../studentPanelFacsimile'
import { regionAttrs } from './previewRegions'
import { runPythonPreview } from './pythonRunner'
import { completeTestResults, parseCppHarness } from '../../testResults'
import { runCPP, runCode } from '../../../../service/codeService'
import '../../../../css/studentview-panel.scss'
import '../../../../css/livepreview-panel.scss'

// Narrower than StudentPanel's 500/500 so the code column keeps a usable width in a
// 1100-1400px preview.
const LEFT_WIDTH = 380
const CHAT_WIDTH = 360

const PREVIEW_ONLY_TIP = 'Not available in the preview'
const NEEDS_SESSION_NOTE = 'Run works once the session is created (after the Language step).'
const NO_RUNNER_NOTE = 'Could not reach the code runner. Check your connection and try again.'

// tool/progressUnit.getPassrateByResult, inlined: that module also pulls in langchain.
const passrateOf = (results) => {
  if (!Array.isArray(results) || results.length === 0) return 0
  return Math.round((results.filter(Boolean).length / results.length) * 100)
}

// Which run path CodeStu takes for the type.
const runKindFor = (type) => {
  const t = type || ''
  if (t === 'CPP') return 'cpp'
  if (t.startsWith('SQL')) return 'sql'
  if (t.startsWith('Java')) return 'java'
  return 'python'
}

// ---------------------------------------------------------------------------------------
// CodeBar (src/component/code/CodeBar.jsx), student buttons - live.
// Run / Reset Code / Latest code act on the preview. Get Stats and Ask for help read or
// write class data, so they are shown exactly when the real bar shows them, but disabled.
function LiveCodeBar({
  onRun,
  onReset,
  onLatest,
  isRunning,
  runBlocked,
  showLatestCode,
  passrate,
  helpButton,
  rootProps,
}) {
  return (
    <div className="codeBar" {...rootProps}>
      <div className="codeBar">
        <div />
        <div
          style={{
            marginLeft: '5px',
            marginRight: '5px',
            display: 'flex',
            justifyContent: 'space-evenly',
            alignContent: 'center',
          }}>
          <button
            type="button"
            className="codeButton"
            onClick={onRun}
            disabled={isRunning || runBlocked}>
            <PlayArrowRoundedIcon sx={{ marginRight: 1, color: '#00921A' }} /> Run
          </button>
          <button type="button" className="codeButton" onClick={onReset}>
            <HistoryIcon sx={{ marginRight: 1, color: '#EAAE00' }} /> Reset Code
          </button>
          {passrate === 100 && (
            <Tooltip title={PREVIEW_ONLY_TIP}>
              <span className="svp-codebar-tip">
                <button type="button" className="codeButton" disabled>
                  <AnalyticsIcon sx={{ marginRight: 1, color: '#00A3EA' }} /> Get Stats
                </button>
              </span>
            </Tooltip>
          )}
          {showLatestCode && (
            <button type="button" className="codeButton" onClick={onLatest}>
              <RestartAltIcon sx={{ marginRight: 1, color: '#2473DE' }} /> Latest code
            </button>
          )}
        </div>
      </div>
      {helpButton && (
        <div>
          <Tooltip title={PREVIEW_ONLY_TIP}>
            <span className="svp-codebar-tip">
              <button type="button" className="codeButton" disabled>
                <HandshakeIcon sx={{ marginRight: 1, color: '#55D246' }} />{' '}
                {passrate === 100 ? 'Offer Help to Others' : 'Ask for help'}
              </button>
            </span>
          </Tooltip>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------------------
// CodeArea (src/component/code/CodeArea.jsx), text-editor branch - editable. Same wrapper
// boxes, python() highlighting and default light theme the student editor uses for every
// StudentPanel type (CPP and SQL included).
function LiveCodeEditor({ code, onChange, rootProps }) {
  const extensions = useMemo(() => [python(), indentUnit.of('    ')], [])
  return (
    <div className="full-size" style={{ height: '250px' }} {...rootProps}>
      <div className="code-mirror-wrapper">
        <span>
          <CodeMirror
            className="codeTextArea"
            value={code}
            extensions={extensions}
            onChange={onChange}
          />
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------
// Blockly: a real BlockMirror (window.BlockMirror, loaded by public/index.html), configured
// as CodeArea configures the student's (toolbox 'ct2', split view, 500px).
//
// Not mounted through BlockpyMirror: that component finds its container with
// document.getElementById and never tears the editor down, and BlockMirror registers
// window 'resize' listeners it never removes. This wrapper passes the container element
// directly, records the window listeners added while the editor is built and removes
// BlockMirror's own on unmount, and disposes the Blockly workspace.
//
// FitViewport scales the whole preview with a CSS transform. Blockly 3 turns a mouse drag
// into workspace units by dividing by its own zoom only, so under a CSS scale dragged
// blocks would trail the pointer. ensureBlocklyScaleCompensation wraps the three drag
// conversions once per page; the wrappers only change anything for a workspace that
// carries lppCssScale, i.e. this preview's - every other Blockly editor is untouched.

let blockMirrorSeq = 0
let blocklyScalePatched = false

const cssScaleOf = (workspace) => {
  if (!workspace) return 1
  const own = workspace.lppCssScale
  if (own) return own
  const parent = workspace.options && workspace.options.parentWorkspace
  return (parent && parent.lppCssScale) || 1
}

function ensureBlocklyScaleCompensation() {
  const Blockly = window.Blockly
  if (blocklyScalePatched || !Blockly || !Blockly.utils || !Blockly.utils.Coordinate) return
  blocklyScalePatched = true
  const wrap = (proto, name) => {
    if (!proto || typeof proto[name] !== 'function') return
    const original = proto[name]
    proto[name] = function lppScaledDelta(delta, ...rest) {
      const k = cssScaleOf(this.workspace_)
      if (!delta || Math.abs(k - 1) < 0.001) return original.call(this, delta, ...rest)
      const scaled = new Blockly.utils.Coordinate(delta.x / k, delta.y / k)
      return original.call(this, scaled, ...rest)
    }
  }
  wrap(Blockly.BlockDragger && Blockly.BlockDragger.prototype, 'pixelsToWorkspaceUnits_')
  wrap(Blockly.WorkspaceDragger && Blockly.WorkspaceDragger.prototype, 'drag')
  wrap(Blockly.FlyoutDragger && Blockly.FlyoutDragger.prototype, 'drag')
}

// BlockMirror's two window 'resize' listeners are `this.method.bind(this)` functions.
// Everything else added to window while it builds (CodeMirror 5's once-per-page global
// handlers, Blockly's per-workspace resize handler, which dispose() removes) is left alone.
const isBlockMirrorListener = (type, listener) =>
  type === 'resize' && typeof listener === 'function' && /^bound /.test(listener.name || '')

function LiveBlockMirror({ seedCode, seedVersion, onCodeChange, onUnavailable }) {
  const hostRef = useRef(null)
  const mirrorRef = useRef(null)
  const seedingRef = useRef(false)
  const onChangeRef = useRef(onCodeChange)
  onChangeRef.current = onCodeChange
  const onUnavailableRef = useRef(onUnavailable)
  onUnavailableRef.current = onUnavailable
  const seedRef = useRef(seedCode)
  seedRef.current = seedCode
  // The code the editor last held (seeded or reported). Change events that carry the same
  // text are not student edits (e.g. a re-render of an unchanged workspace) and are dropped,
  // so they cannot stop the preview from following the starter.
  const lastValueRef = useRef(null)
  const [divId] = useState(() => `blockmirror-live-preview-${(blockMirrorSeq += 1)}`)

  useEffect(() => {
    const host = hostRef.current
    const Blockly = window.Blockly
    if (!host || typeof window.BlockMirror !== 'function' || !Blockly) {
      onUnavailableRef.current?.()
      return undefined
    }
    ensureBlocklyScaleCompensation()

    const added = []
    const originalAdd = window.addEventListener
    let mirror = null
    try {
      window.addEventListener = function recordListener(type, listener, options) {
        added.push([type, listener, options])
        return originalAdd.call(this, type, listener, options)
      }
      mirror = new window.BlockMirror({
        container: host,
        toolbox: 'ct2',
        blocklyMediaPath: '../lib/blockly/media/',
        viewMode: 'split',
      })
    } catch (error) {
      console.warn('Preview: BlockMirror failed to start', error)
      mirror = null
    } finally {
      window.addEventListener = originalAdd
    }

    const workspace = mirror?.blockEditor?.workspace || null
    const measureScale = () => {
      if (!workspace) return
      const width = host.offsetWidth
      const scale = width ? host.getBoundingClientRect().width / width : 1
      workspace.lppCssScale = scale > 0 ? scale : 1
    }
    let frame = 0
    let observer = null

    // BlockMirror's Python text pane is CodeMirror 5, which does not handle a scaled
    // (CSS-transformed) ancestor: under the preview's scale a click lands on the wrong line,
    // further off the lower you click. While scaled, place the caret ourselves from
    // unscaled coordinates. (Drag-selection is not corrected; click and shift+click are.)
    const textCm = mirror?.textEditor?.codeMirror || null
    const textWrapper = textCm?.getWrapperElement?.() || null
    const correctTextClick = (e) => {
      if (!textCm || e.button !== 0) return
      const width = host.offsetWidth
      const k = width ? host.getBoundingClientRect().width / width : 1
      if (!k || Math.abs(k - 1) < 0.01) return
      const display = textCm.display
      if (!display?.scroller?.contains(e.target) || e.target.closest?.('.CodeMirror-gutters')) return
      e.preventDefault()
      e.stopPropagation()
      const lineSpace = display.lineSpace.getBoundingClientRect()
      const pos = textCm.coordsChar(
        { left: (e.clientX - lineSpace.left) / k, top: (e.clientY - lineSpace.top) / k },
        'div'
      )
      textCm.focus()
      if (e.shiftKey) textCm.setSelection(textCm.getCursor('anchor'), pos)
      else textCm.setCursor(pos)
    }

    const teardown = () => {
      if (observer) observer.disconnect()
      cancelAnimationFrame(frame)
      textWrapper?.removeEventListener('mousedown', correctTextClick, true)
      host.removeEventListener('pointerdown', measureScale, true)
      host.removeEventListener('mousedown', measureScale, true)
      // A text edit schedules a deferred text-to-blocks update; do not let it run against
      // a disposed workspace.
      try {
        if (mirror?.textEditor?.updateTimer_ != null) clearTimeout(mirror.textEditor.updateTimer_)
      } catch (error) {
        // nothing pending
      }
      added.forEach(([type, listener, options]) => {
        if (isBlockMirrorListener(type, listener)) {
          window.removeEventListener(type, listener, options)
        }
      })
      try {
        mirror?.removeAllChangeListeners()
      } catch (error) {
        // already gone
      }
      if (workspace) {
        try {
          Blockly.hideChaff(true)
        } catch (error) {
          // nothing open
        }
        try {
          workspace.dispose()
        } catch (error) {
          console.warn('Preview: Blockly workspace dispose failed', error)
        }
        // Blockly.inject made this workspace the page's main one; hand that back to another
        // live editor (e.g. the wizard's Code Stubs template) or clear it, as before inject.
        if (Blockly.mainWorkspace === workspace) {
          const others = (Blockly.Workspace?.getAll?.() || []).filter(
            (ws) => ws !== workspace && ws.rendered && !ws.isFlyout && !ws.options?.parentWorkspace
          )
          Blockly.mainWorkspace = others[0] || null
        }
      }
      host.innerHTML = ''
      mirrorRef.current = null
    }

    if (!mirror || !workspace) {
      teardown()
      onUnavailableRef.current?.()
      return undefined
    }

    mirrorRef.current = mirror
    measureScale()
    try {
      seedingRef.current = true
      lastValueRef.current = seedRef.current || ''
      mirror.setCode(seedRef.current || '')
    } catch (error) {
      console.warn('Preview: could not load the starter into Blockly', error)
    } finally {
      seedingRef.current = false
    }
    mirror.addChangeListener((event) => {
      if (seedingRef.current) return
      const value = event?.value ?? ''
      if (value === lastValueRef.current) return
      lastValueRef.current = value
      onChangeRef.current?.(value)
    })
    host.addEventListener('pointerdown', measureScale, true)
    host.addEventListener('mousedown', measureScale, true)
    textWrapper?.addEventListener('mousedown', correctTextClick, true)
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        cancelAnimationFrame(frame)
        frame = requestAnimationFrame(() => {
          measureScale()
          try {
            mirror.refresh()
          } catch (error) {
            // detached
          }
        })
      })
      observer.observe(host)
    }
    return teardown
  }, [])

  // Re-seed (starter changed while unedited, Reset Code, Latest code).
  const firstSeedRef = useRef(true)
  useEffect(() => {
    if (firstSeedRef.current) {
      firstSeedRef.current = false
      return
    }
    const mirror = mirrorRef.current
    if (!mirror) return
    try {
      seedingRef.current = true
      lastValueRef.current = seedRef.current || ''
      mirror.setCode(seedRef.current || '')
    } catch (error) {
      console.warn('Preview: could not load code into Blockly', error)
    } finally {
      seedingRef.current = false
    }
  }, [seedVersion])

  return <div id={divId} ref={hostRef} className="lpp-blockly-host" style={{ height: 500 }} />
}

// CodeArea's Blockly branch boxes: a 500px .full-size holding the editor, then the empty
// .code-mirror-wrapper the real component still renders under it.
function LiveBlocklyEditor({ code, seedVersion, onChange, rootProps }) {
  const [unavailable, setUnavailable] = useState(false)
  const handleUnavailable = useCallback(() => setUnavailable(true), [])
  if (unavailable) {
    return (
      <>
        <div className="lpp-note">
          The block editor could not start in the preview, so the Python it produces is shown
          as text instead. Students get blocks.
        </div>
        <LiveCodeEditor code={code} onChange={onChange} rootProps={rootProps} />
      </>
    )
  }
  return (
    <div className="full-size" style={{ height: '500px' }} {...rootProps}>
      <span>
        <LiveBlockMirror
          seedCode={code}
          seedVersion={seedVersion}
          onCodeChange={onChange}
          onUnavailable={handleUnavailable}
        />
      </span>
      <div className="code-mirror-wrapper" />
    </div>
  )
}

// ---------------------------------------------------------------------------------------
// CodeStu's SQL result diff (second .container): "Your Output" cells red where they do not
// match the expected tables, "Expected Output" cells green where the student's differ.
// Same comparisons as CodeStu, guarded so malformed results cannot throw.
function indexOfArray(val, array) {
  const hash = {}
  const list = Array.isArray(array) ? array : []
  for (let i = 0; i < list.length; i++) hash[list[i]] = i
  return Object.prototype.hasOwnProperty.call(hash, val) ? hash[val] : -1
}

const WHITE = '#ffffff'
const RED = '#EE4B2B'
const GREEN = '#32CD32'

function SqlResultView({ results, expected, rootProps }) {
  return (
    <span>
      <div style={{ display: 'flex', overflowX: 'scroll', marginTop: '.5em' }} {...rootProps}>
        <div>
          <Typography>Your Output</Typography>
          <div>
            <div>
              <Grid container>
                {results.map(({ columns = [], values = [], name }, i) => (
                  <table key={i}>
                    <caption>{name}</caption>
                    <thead>
                      <tr>
                        {columns.map((columnName, j) => {
                          const same =
                            expected[i]?.columns?.[j] !== undefined &&
                            expected[i]?.columns?.[j] === results[i]?.columns?.[j]
                          return (
                            <td key={j} style={{ backgroundColor: same ? WHITE : RED }}>
                              {columnName}
                            </td>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {values.map((row, j) => (
                        <tr key={j}>
                          {(row || []).map((value, k) => {
                            const ok =
                              results[i] !== undefined &&
                              (indexOfArray(results[i]?.values?.[j], expected[i]?.values) !== -1 ||
                                (expected[i]?.values?.[j] !== undefined &&
                                  expected[i]?.values?.[j]?.[k] !== undefined &&
                                  Boolean(expected[i]?.values?.[j]?.[k])))
                            return (
                              <td key={k} style={{ backgroundColor: ok ? WHITE : RED }}>
                                {value}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}
              </Grid>
            </div>
          </div>
        </div>
        <Divider orientation="vertical" flexItem />
        <div>
          {expected.length > 0 && <Typography>Expected Output</Typography>}
          <div>
            <div>
              <Grid container>
                {expected.map(({ columns = [], values = [], name }, i) => (
                  <table key={i}>
                    <caption>{name}</caption>
                    <thead>
                      <tr>
                        {columns.map((columnName, j) => {
                          const same =
                            results[i] !== undefined &&
                            results[i]?.columns?.[j] !== undefined &&
                            results[i]?.columns?.[j] === expected[i]?.columns?.[j]
                          return (
                            <td key={j} style={{ backgroundColor: same ? WHITE : GREEN }}>
                              {columnName}
                            </td>
                          )
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {values.map((row, j) => (
                        <tr key={j}>
                          {(row || []).map((value, k) => {
                            const ok =
                              results[i] !== undefined &&
                              (indexOfArray(expected[i]?.values?.[j], results[i]?.values) !== -1 ||
                                (results[i]?.values?.[j] !== undefined &&
                                  results[i]?.values?.[j]?.[k] !== undefined &&
                                  results[i]?.values?.[j]?.[k] === expected[i]?.values?.[j]?.[k]))
                            return (
                              <td key={k} style={{ backgroundColor: ok ? WHITE : GREEN }}>
                                {value}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ))}
              </Grid>
            </div>
          </div>
        </div>
      </div>
    </span>
  )
}

// CodeStu.runSQL's reading of runCode's answer.
const readSqlResult = (result, type) => {
  if (!result || typeof result !== 'object') {
    return { output: NO_RUNNER_NOTE, tables: [], expected: [], passrate: 0 }
  }
  if (result.message != null) {
    return { output: String(result.message), tables: [], expected: [], passrate: 0 }
  }
  const passrate = Number(result.result) || 0
  if (passrate === 100) return { output: 'Success', tables: [], expected: [], passrate }
  let tables = Array.isArray(result.data) ? result.data : []
  let expected = Array.isArray(result.expected) ? result.expected : []
  if (type === 'SQL Schema') {
    // The first table lists the table names; name the rest after it and drop it.
    const name = (list) =>
      list.slice(1).map((table, i) => ({ ...table, name: list[0]?.values?.[i] }))
    tables = name(tables)
    expected = name(expected)
  }
  return { output: 'Failed', tables, expected, passrate }
}

// CodeStu.runCppCode's reading of runCPP's answer. With tests on, every test ends pass or
// fail whatever happened (compile error, crash, no answer): the harness's 1/0 lines, padded
// to the test list.
const readCppResult = (result, withTests, testCount) => {
  const settle = (stderr) => (withTests ? completeTestResults(parseCppHarness(stderr), testCount) : [])
  if (!result || typeof result !== 'object' || !result.executionResult) {
    const results = settle('')
    return { output: NO_RUNNER_NOTE, results, passrate: passrateOf(results) }
  }
  const stdout = String(result.executionResult.stdout ?? '')
  const stderr = String(result.executionResult.stderr ?? '')
  if (result.isSucceed && !withTests) {
    // Tests off: stderr is just program output, not one pass/fail line per test.
    const joined = stderr ? `${stdout}${stdout && !stdout.endsWith('\n') ? '\n' : ''}${stderr}` : stdout
    return { output: joined, results: [], passrate: 0 }
  }
  if (result.isSucceed) {
    const results = settle(stderr)
    return { output: stdout, results, passrate: passrateOf(results) }
  }
  // Non-zero exit. With tests off that is ordinary program behaviour, so keep what the
  // program printed; compile errors have no stdout.
  const output = !withTests && stdout ? `${stdout}${stdout.endsWith('\n') ? '' : '\n'}${stderr}` : stderr
  const results = settle(stderr)
  return { output, results, passrate: passrateOf(results) }
}

// ---------------------------------------------------------------------------------------

function StudentPanelLive({ session, withTests, highlight }) {
  const type = session?.type || ''
  const kind = runKindFor(type)
  const isSql = kind === 'sql'
  const isBlockly = type.startsWith('Blockly')
  const isHelper = type === 'Helper/Helpee'
  const sessionId = session?.id
  const starter = session?.test_code?.template ?? ''

  const [code, setCode] = useState(starter)
  const [seedVersion, setSeedVersion] = useState(0)
  const [output, setOutput] = useState('')
  const [testResults, setTestResults] = useState(null)
  const [sqlTables, setSqlTables] = useState({ results: [], expected: [] })
  const [isRunning, setIsRunning] = useState(false)
  // selectedCode.passrate / latestCodeId !== selectedCode.id in the real bar.
  const [passrate, setPassrate] = useState(0)
  const [showLatestCode, setShowLatestCode] = useState(true)
  const [latestRun, setLatestRun] = useState(null)

  const editedRef = useRef(false)
  const mountedRef = useRef(true)
  const runSeqRef = useRef(0)
  const runningRef = useRef(false)
  const latestRef = useRef({})
  latestRef.current = { code, session, withTests, kind, sessionId }

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const loadCode = useCallback((text) => {
    setCode(text)
    setSeedVersion((v) => v + 1)
  }, [])

  // The starter changed (the instructor is editing it): follow it until the preview's
  // code has been edited.
  const firstStarterRef = useRef(true)
  useEffect(() => {
    if (firstStarterRef.current) {
      firstStarterRef.current = false
      return
    }
    if (!editedRef.current) loadCode(starter)
  }, [starter, loadCode])

  const passrateRef = useRef(passrate)
  passrateRef.current = passrate

  const handleCodeChange = useCallback(
    (value) => {
      editedRef.current = true
      setCode(value)
      // CodeStu.handleBlocklyChange saves every Blockly edit as the student's latest code
      // (with the current pass rate), so the real bar offers "Latest code" again and it
      // loads that edit. Text editors only save on Run.
      if (isBlockly) {
        setLatestRun({ code: value, passrate: passrateRef.current })
        setShowLatestCode(true)
      }
    },
    [isBlockly]
  )

  // CodeBar.resetCode: back to the template; output and test results stay. The real reset
  // also saves the template as the student's latest code, so "Latest code" then loads it.
  const handleReset = useCallback(() => {
    editedRef.current = false
    loadCode(starter)
    setPassrate(0)
    setLatestRun({ code: starter, passrate: 0 })
    setShowLatestCode(true)
  }, [loadCode, starter])

  // CodeBar.handleLatestCode: the student's latest saved code (last run, Blockly edit or
  // reset), or the template if none.
  const handleLatest = useCallback(() => {
    if (latestRun) {
      editedRef.current = latestRun.code !== starter
      loadCode(latestRun.code)
      setPassrate(latestRun.passrate)
    } else {
      editedRef.current = false
      loadCode(starter)
      setPassrate(0)
    }
    setShowLatestCode(false)
  }, [latestRun, loadCode, starter])

  const runBlocked = kind === 'java' || ((kind === 'cpp' || kind === 'sql') && !sessionId)
  const runNote =
    kind === 'java'
      ? 'Running Java is not available in the preview.'
      : runBlocked
      ? NEEDS_SESSION_NOTE
      : ''

  const handleRun = useCallback(async () => {
    const current = latestRef.current
    // A ref, not the isRunning state: a double click lands before the re-render that
    // disables the button.
    if (runningRef.current) return
    runningRef.current = true
    const seq = (runSeqRef.current += 1)
    const live = () => mountedRef.current && runSeqRef.current === seq
    const ranCode = current.code
    const testCode = current.session?.test_code
    const testCount = Array.isArray(current.session?.test_list) ? current.session.test_list.length : 0
    setIsRunning(true)
    let nextPassrate = 0
    try {
      if (current.kind === 'cpp') {
        if (!current.sessionId) return
        setOutput('')
        // Follow the test switch as drafted in the wizard, not the saved row: the server
        // otherwise reads enable_tests from the database, which only changes on Continue.
        const result = await runCPP(ranCode, current.sessionId, { withHarness: Boolean(current.withTests) })
        if (!live()) return
        const read = readCppResult(result, current.withTests, testCount)
        setOutput(read.output)
        setTestResults(read.results)
        nextPassrate = read.passrate
      } else if (current.kind === 'sql') {
        if (!current.sessionId) return
        const result = await runCode(
          ranCode,
          testCode?.unit_test ?? '',
          current.session?.type === 'SQL Schema' ? 'Schema' : 'Select',
          current.sessionId
        )
        if (!live()) return
        const read = readSqlResult(result, current.session?.type)
        setOutput(read.output)
        setSqlTables({ results: read.tables, expected: read.expected })
        nextPassrate = read.passrate
      } else if (current.kind === 'python') {
        // CodeStu's Python path (Blockly, Auto Grouping, Helper/Helpee): student code,
        // then the variable-setup starter, then the hidden harness; `result` read back.
        const { stdout, error, results } = await runPythonPreview({
          code: ranCode,
          starter: testCode?.starter ?? '',
          harness: testCode?.unit_test ?? '',
          withTests: current.withTests,
        })
        if (!live()) return
        setOutput(error || stdout)
        // Every test ends pass or fail, even when the run stopped before the harness.
        if (current.withTests) {
          const settled = completeTestResults(results, testCount)
          setTestResults(settled)
          nextPassrate = passrateOf(settled)
        }
      } else {
        return
      }
      if (!live()) return
      setPassrate(nextPassrate)
      setLatestRun({ code: ranCode, passrate: nextPassrate })
      setShowLatestCode(false)
    } catch (error) {
      if (!live()) return
      setOutput(`Could not run the code: ${error?.message || error}`)
      // The runner itself failed: with tests on, that run fails every test.
      if (current.withTests && (current.kind === 'python' || current.kind === 'cpp')) {
        setTestResults(completeTestResults([], testCount))
      }
    } finally {
      runningRef.current = false
      if (live()) setIsRunning(false)
    }
  }, [])

  // --- columns ---------------------------------------------------------------------------

  const left = (
    <>
      <TaskCardFacsimile
        subject={session?.subject || ''}
        task={session?.task || ''}
        rootProps={regionAttrs('task', highlight)}
      />
      {isSql ? (
        <SqlSchemaFacsimile output={output} rootProps={regionAttrs('tests', highlight)} />
      ) : (
        withTests && (
          <TestTableFacsimile
            testList={Array.isArray(session?.test_list) ? session.test_list : []}
            results={testResults}
            rootProps={regionAttrs('tests', highlight)}
          />
        )
      )}
    </>
  )

  const editorProps = regionAttrs('editor', highlight)
  const runProps = regionAttrs('run', highlight)

  const codeColumn = (
    <CodeStuFrame
      after={
        isSql ? (
          <SqlResultView
            results={sqlTables.results}
            expected={sqlTables.expected}
            rootProps={runProps}
          />
        ) : null
      }>
      <LiveCodeBar
        onRun={handleRun}
        onReset={handleReset}
        onLatest={handleLatest}
        isRunning={isRunning}
        runBlocked={runBlocked}
        showLatestCode={showLatestCode}
        passrate={passrate}
        helpButton={isSql || isHelper}
        rootProps={runProps}
      />
      {runNote && <div className="lpp-note">{runNote}</div>}
      {isBlockly ? (
        <LiveBlocklyEditor
          code={code}
          seedVersion={seedVersion}
          onChange={handleCodeChange}
          rootProps={editorProps}
        />
      ) : (
        <LiveCodeEditor code={code} onChange={handleCodeChange} rootProps={editorProps} />
      )}
      {!isSql && <OutputFacsimile output={output} rootProps={runProps} />}
    </CodeStuFrame>
  )

  const chatFacsimile = studentPanelChatFacsimile(type, Boolean(session?.enable_chat))
  const chat =
    chatFacsimile == null ? null : (
      <>
        <div className="lpp-chat-note">
          Peer chat needs a group of students, so it is not available in the preview.
        </div>
        {chatFacsimile}
      </>
    )

  return (
    <>
      <AppBarFacsimile />
      <div className="svp-home-container">
        <StudentPanelShell
          top={<PeerInstructionFacsimile activeStep={isHelper ? 1 : 0} />}
          left={left}
          code={codeColumn}
          chat={chat}
          chatProps={regionAttrs('chat', highlight)}
          leftWidth={LEFT_WIDTH}
          chatWidth={CHAT_WIDTH}
          height="calc(85 * var(--svp-vh, 1vh))"
        />
      </div>
      {isRunning && (
        <div className="lpp-toast" role="status">
          Executing code...
        </div>
      )}
    </>
  )
}

// Props (the live-preview contract):
//   session       the wizard's merged draft session; id is absent before the row exists
//   testsEnabled  whether the exercise uses test cases
//   highlight     region ids to highlight (previewRegions.js)
//   resetToken    a change resets everything the instructor did in the preview
//   chatEnabled, onBeforeChat  accepted and ignored: these types have no AI chat
export default function InteractiveStudentPanelPreview({
  session,
  testsEnabled = true,
  highlight = [],
  resetToken,
}) {
  const rootRef = useRef(null)
  const [unit, setUnit] = useState(null)

  // The student stylesheets size columns in vh/vw (85vh columns, an output box of
  // calc(80vh - 260px), ...). Pin those units to this preview's box, as .svp-screen does
  // for the read-only preview, so the layout is the one a student with a window this size
  // gets.
  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return undefined
    const measure = () => {
      const width = root.clientWidth
      const height = root.clientHeight
      if (width > 0 && height > 0) setUnit({ vw: width / 100, vh: height / 100 })
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(root)
    return () => observer.disconnect()
  }, [])

  // Everything the instructor did is per (reset, session type): a new token or a different
  // type remounts the panel, which drops all of its state and tears the editors down.
  const generationRef = useRef({ token: resetToken, type: session?.type, n: 0 })
  if (
    generationRef.current.token !== resetToken ||
    generationRef.current.type !== session?.type
  ) {
    generationRef.current = {
      token: resetToken,
      type: session?.type,
      n: generationRef.current.n + 1,
    }
  }

  const style = unit ? { '--svp-vh': `${unit.vh}px`, '--svp-vw': `${unit.vw}px` } : undefined

  return (
    <div
      ref={rootRef}
      className="svp-screen lpp-root"
      style={style}
      aria-label="Interactive student view preview">
      <StudentPanelLive
        key={generationRef.current.n}
        session={session}
        withTests={testsEnabled !== false}
        highlight={Array.isArray(highlight) ? highlight : []}
      />
    </div>
  )
}
